import { memo, useEffect, useState } from 'react'
import { CTCGlobalIcon } from './icons/CTCGlobalIcon'
import { scrollToSection } from '../utils/scroll'

const LiveClock = memo(function LiveClock() {
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <div className="time-display-bento">
      <div className="time-large">
        {currentTime.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })}
      </div>
      <div className="time-location">
        <span className="location-pin">📍</span>
        Newport Beach, CA
      </div>
      <div className="time-date">
        {currentTime.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        })}
      </div>
    </div>
  )
})

export const Hero = memo(function Hero() {
  return (
    <section id="hero" className="hero-bento">
      <div className="container-bento">
        <div className="bento-grid hero-grid">
          {/* Main Hero Card */}
          <div className="bento-card hero-main">
            <div className="hero-content-bento">
              <div className="hero-top-row">
                <div className="hero-avatar">
                  <img
                    src="https://avatars.githubusercontent.com/u/23286067?v=4"
                    alt="Liam Kande"
                    className="avatar-image"
                  />
                </div>
                <div className="status-badge-bento">
                  <span className="status-dot-bento"></span>
                  Available
                </div>
              </div>

              <h1 className="hero-title-bento">
                Liam Kande
                <span className="title-subtitle">Staff Software Engineer</span>
              </h1>
              <p className="hero-description-bento">
                Founding engineer at <strong>CTC Global</strong> • <strong>MIT Graduate</strong> •{' '}
                <strong>Master's in IT</strong>
                <br />
                Full stack · cloud architecture · security &amp; compliance
              </p>
              <div className="hero-actions-bento">
                <button className="btn-bento primary" onClick={() => scrollToSection('journey')}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M10 3v14M3 10l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  View Journey
                </button>
                <a
                  href="https://firebasestorage.googleapis.com/v0/b/liam-portfolio.appspot.com/o/Liam-Kande-Resume.pdf?alt=media&token=c6413292-e0fd-4d92-b80f-53b8abd01e35"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-bento resume"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      d="M6 2h8l4 4v11a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 2v4h4M12 11H8M12 15H8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  View Resume
                </a>
                <button className="btn-bento secondary" onClick={() => scrollToSection('contact')}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 8l7-5 7 5v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" strokeLinecap="round" />
                    <path d="M10 18V10" strokeLinecap="round" />
                  </svg>
                  Get In Touch
                </button>
              </div>
            </div>
          </div>

          {/* Stats Card with Rocket Icon */}
          <div className="bento-card stats-card">
            <div className="card-header-bento">
              <h3>Impact</h3>
              <span className="card-icon rocket-icon">
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="rocketGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="rocketGradientHover" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#a78bfa', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#818cf8', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>

                  <circle cx="15" cy="20" r="1.5" className="rocket-star star-1" />
                  <circle cx="85" cy="30" r="1" className="rocket-star star-2" />
                  <circle cx="25" cy="45" r="1.2" className="rocket-star star-3" />

                  <ellipse cx="50" cy="40" rx="12" ry="20" className="rocket-body" />
                  <path d="M 50 10 L 62 30 L 38 30 Z" className="rocket-body" />
                  <circle cx="50" cy="35" r="5" className="rocket-window" />
                  <path d="M 38 50 L 30 65 L 38 60 Z" className="rocket-fin" />
                  <path d="M 62 50 L 70 65 L 62 60 Z" className="rocket-fin" />

                  <g className="rocket-flames">
                    <ellipse cx="50" cy="68" rx="4" ry="12" className="rocket-flame flame-1" />
                    <ellipse cx="44" cy="66" rx="3" ry="10" className="rocket-flame flame-2" />
                    <ellipse cx="56" cy="66" rx="3" ry="10" className="rocket-flame flame-3" />
                  </g>
                </svg>
              </span>
            </div>
            <div className="stats-grid-bento">
              <div className="stat-item-bento">
                <div className="stat-value-bento">$60M+</div>
                <div className="stat-label-bento">Savings &amp; Revenue Delivered</div>
              </div>
              <div className="stat-item-bento">
                <div className="stat-value-bento">Month 1</div>
                <div className="stat-label-bento">
                  Secured Multi-Million Dollar Google Partnership
                </div>
              </div>
              <div className="stat-item-bento">
                <div className="stat-value-bento">5</div>
                <div className="stat-label-bento">NERC CIP Standards Implemented</div>
              </div>
              <div className="stat-item-bento">
                <div className="stat-value-bento">10–100×</div>
                <div className="stat-label-bento">Warehouse Scan Reduction</div>
              </div>
            </div>
          </div>

          {/* Current Time/Location Card with Rotating World */}
          <div className="bento-card time-card">
            <div className="card-header-bento">
              <h3>Current Time</h3>
              <span className="card-icon globe-icon">
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="globeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
                      <stop offset="50%" style={{ stopColor: '#2563eb', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#1d4ed8', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="oceanGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 0.6 }} />
                      <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0.8 }} />
                    </linearGradient>
                  </defs>

                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="url(#globeGradient)"
                    className="globe-sphere"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="url(#oceanGradient)"
                    className="globe-ocean"
                    opacity="0.5"
                  />

                  <ellipse
                    cx="50"
                    cy="35"
                    rx="35"
                    ry="8"
                    fill="none"
                    stroke="#1e40af"
                    strokeWidth="0.5"
                    className="latitude-line"
                    opacity="0.4"
                  />
                  <ellipse
                    cx="50"
                    cy="50"
                    rx="35"
                    ry="10"
                    fill="none"
                    stroke="#1e40af"
                    strokeWidth="0.8"
                    className="latitude-line"
                    opacity="0.6"
                  />
                  <ellipse
                    cx="50"
                    cy="65"
                    rx="35"
                    ry="8"
                    fill="none"
                    stroke="#1e40af"
                    strokeWidth="0.5"
                    className="latitude-line"
                    opacity="0.4"
                  />

                  <g className="longitude-lines">
                    <ellipse
                      cx="50"
                      cy="50"
                      rx="10"
                      ry="35"
                      fill="none"
                      stroke="#1e40af"
                      strokeWidth="0.8"
                      opacity="0.6"
                    />
                    <ellipse
                      cx="50"
                      cy="50"
                      rx="20"
                      ry="35"
                      fill="none"
                      stroke="#1e40af"
                      strokeWidth="0.6"
                      opacity="0.5"
                    />
                    <ellipse
                      cx="50"
                      cy="50"
                      rx="30"
                      ry="35"
                      fill="none"
                      stroke="#1e40af"
                      strokeWidth="0.5"
                      opacity="0.4"
                    />
                  </g>

                  <g className="continents">
                    <path
                      d="M 35 30 Q 30 35 32 40 L 38 38 Q 40 35 35 30 Z"
                      fill="#10b981"
                      opacity="0.8"
                    />
                    <path
                      d="M 40 48 Q 38 52 40 58 L 45 56 Q 44 50 40 48 Z"
                      fill="#10b981"
                      opacity="0.8"
                    />
                    <path
                      d="M 48 32 Q 52 30 56 32 L 58 38 Q 56 45 52 48 L 50 45 Q 48 38 48 32 Z"
                      fill="#10b981"
                      opacity="0.8"
                    />
                    <path
                      d="M 60 28 Q 68 30 70 35 L 68 42 Q 65 45 60 43 L 58 38 Q 60 32 60 28 Z"
                      fill="#10b981"
                      opacity="0.8"
                    />
                  </g>

                  <ellipse
                    cx="50"
                    cy="50"
                    rx="42"
                    ry="42"
                    fill="none"
                    stroke="#60a5fa"
                    strokeWidth="0.5"
                    className="orbit-ring"
                    opacity="0.3"
                  />

                  <circle cx="85" cy="50" r="2" fill="#fbbf24" className="satellite">
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 50 50"
                      to="360 50 50"
                      dur="8s"
                      repeatCount="indefinite"
                    />
                  </circle>

                  <g className="location-marker">
                    <path
                      d="M 50 42 Q 48 40 48 38 Q 48 36 50 36 Q 52 36 52 38 Q 52 40 50 42 L 50 45 Z"
                      fill="#ef4444"
                      stroke="#dc2626"
                      strokeWidth="0.5"
                    />
                    <circle cx="50" cy="38" r="1.5" fill="#fef2f2" />
                  </g>

                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="#60a5fa"
                    strokeWidth="0.5"
                    className="globe-glow"
                    opacity="0.3"
                  />
                </svg>
              </span>
            </div>
            <LiveClock />
          </div>

          {/* Quick Info Card with Animated Briefcase */}
          <div className="bento-card info-card">
            <div className="card-header-bento">
              <h3>Currently</h3>
              <span className="card-icon briefcase-icon">
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="briefcaseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#d97706', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="briefcaseHandleGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#b45309', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#92400e', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>

                  <rect
                    x="20"
                    y="40"
                    width="60"
                    height="40"
                    rx="4"
                    fill="url(#briefcaseGradient)"
                    className="briefcase-body"
                  />
                  <rect
                    x="20"
                    y="35"
                    width="60"
                    height="5"
                    rx="2"
                    fill="#b45309"
                    className="briefcase-lid"
                  />
                  <path
                    d="M 35 35 Q 35 25 50 25 Q 65 25 65 35"
                    fill="none"
                    stroke="url(#briefcaseHandleGradient)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    className="briefcase-handle"
                  />

                  <rect
                    x="47"
                    y="50"
                    width="6"
                    height="8"
                    rx="1"
                    fill="#92400e"
                    className="briefcase-lock"
                  />
                  <circle cx="50" cy="54" r="2" fill="#fbbf24" className="lock-keyhole" />

                  <rect
                    x="25"
                    y="43"
                    width="8"
                    height="3"
                    rx="1"
                    fill="#92400e"
                    className="briefcase-clasp"
                  />
                  <rect
                    x="67"
                    y="43"
                    width="8"
                    height="3"
                    rx="1"
                    fill="#92400e"
                    className="briefcase-clasp"
                  />

                  <line
                    x1="20"
                    y1="55"
                    x2="80"
                    y2="55"
                    stroke="#d97706"
                    strokeWidth="0.5"
                    opacity="0.4"
                    className="texture-line"
                  />
                  <line
                    x1="20"
                    y1="65"
                    x2="80"
                    y2="65"
                    stroke="#d97706"
                    strokeWidth="0.5"
                    opacity="0.4"
                    className="texture-line"
                  />

                  <circle cx="24" cy="44" r="1.5" fill="#78350f" className="corner-rivet" />
                  <circle cx="76" cy="44" r="1.5" fill="#78350f" className="corner-rivet" />
                  <circle cx="24" cy="76" r="1.5" fill="#78350f" className="corner-rivet" />
                  <circle cx="76" cy="76" r="1.5" fill="#78350f" className="corner-rivet" />

                  <circle cx="15" cy="30" r="1.5" fill="#fbbf24" className="sparkle sparkle-1" />
                  <circle cx="85" cy="35" r="1" fill="#fbbf24" className="sparkle sparkle-2" />
                  <circle cx="10" cy="50" r="1.2" fill="#fbbf24" className="sparkle sparkle-3" />
                </svg>
              </span>
            </div>
            <div className="info-list-bento">
              <div className="info-item-bento">
                <span className="info-emoji">
                  <CTCGlobalIcon />
                </span>
                <div>
                  <div className="info-title">CTC Global</div>
                  <div className="info-subtitle">Founding Engineer &amp; Technical Lead</div>
                </div>
              </div>
              <div className="info-item-bento">
                <div className="info-nike-logo">
                  <svg viewBox="0 0 69 32" className="nike-logo-hero">
                    <path
                      fill="currentColor"
                      d="M68.56 4L18.4 25.36Q12.16 28 7.92 28q-4.8 0-6.96-3.36-1.36-2.16-.8-5.48t2.96-7.08q2-3.04 6.56-8-1.6 2.56-2.24 5.28-1.2 5.12 2.4 7.52Q14.24 19 21.52 19q4.4 0 16.64-3.52L68.56 4z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="info-title">Nike</div>
                  <div className="info-subtitle">Senior Software Engineer</div>
                </div>
              </div>
            </div>
          </div>

          {/* Tech Stack Card with Animated Lightning Bolt */}
          <div className="bento-card tech-card">
            <div className="card-header-bento">
              <h3>Tech Stack</h3>
              <span className="card-icon lightning-icon">
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="lightningGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#fbbf24', stopOpacity: 1 }} />
                      <stop offset="50%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#dc2626', stopOpacity: 1 }} />
                    </linearGradient>
                    <filter id="lightningGlow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Main Lightning Bolt */}
                  <path
                    d="M 55 10 L 35 45 L 48 45 L 40 85 L 75 40 L 58 40 L 55 10 Z"
                    fill="url(#lightningGradient)"
                    className="lightning-bolt"
                    filter="url(#lightningGlow)"
                  />

                  {/* Inner highlight */}
                  <path
                    d="M 53 15 L 40 45 L 48 45 L 45 70"
                    fill="none"
                    stroke="#fef3c7"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="lightning-highlight"
                    opacity="0.8"
                  />

                  {/* Energy particles */}
                  <circle
                    cx="50"
                    cy="25"
                    r="2"
                    fill="#fbbf24"
                    className="energy-particle particle-1"
                  />
                  <circle
                    cx="45"
                    cy="35"
                    r="1.5"
                    fill="#f59e0b"
                    className="energy-particle particle-2"
                  />
                  <circle
                    cx="55"
                    cy="30"
                    r="1.8"
                    fill="#fbbf24"
                    className="energy-particle particle-3"
                  />
                  <circle
                    cx="52"
                    cy="50"
                    r="1.5"
                    fill="#f59e0b"
                    className="energy-particle particle-4"
                  />
                  <circle
                    cx="60"
                    cy="45"
                    r="1.2"
                    fill="#dc2626"
                    className="energy-particle particle-5"
                  />
                  <circle
                    cx="48"
                    cy="60"
                    r="2"
                    fill="#fbbf24"
                    className="energy-particle particle-6"
                  />

                  {/* Electric arcs */}
                  <path
                    d="M 35 45 Q 30 48 28 52"
                    fill="none"
                    stroke="#fbbf24"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    className="electric-arc arc-1"
                    opacity="0.6"
                  />
                  <path
                    d="M 75 40 Q 78 42 80 46"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    className="electric-arc arc-2"
                    opacity="0.6"
                  />

                  {/* Impact sparks at bottom */}
                  <circle
                    cx="40"
                    cy="85"
                    r="3"
                    fill="#fbbf24"
                    className="impact-spark spark-1"
                    opacity="0.8"
                  />
                  <circle
                    cx="35"
                    cy="88"
                    r="2"
                    fill="#f59e0b"
                    className="impact-spark spark-2"
                    opacity="0.6"
                  />
                  <circle
                    cx="45"
                    cy="88"
                    r="2"
                    fill="#dc2626"
                    className="impact-spark spark-3"
                    opacity="0.7"
                  />
                </svg>
              </span>
            </div>
            <div className="tech-tags-bento">
              <span className="tech-tag">Javascript</span>
              <span className="tech-tag">React</span>
              <span className="tech-tag">Next.js</span>
              <span className="tech-tag">TypeScript</span>
              <span className="tech-tag">React Native</span>
              <span className="tech-tag">Node.js</span>
              <span className="tech-tag">GraphQL</span>
              <span className="tech-tag">REST APIs</span>
              <span className="tech-tag">AWS</span>
              <span className="tech-tag">Docker</span>
              <span className="tech-tag">PostgreSQL</span>
              <span className="tech-tag">Firebase</span>
              <span className="tech-tag">Python</span>
              <span className="tech-tag">Terraform</span>
              <span className="tech-tag">TensorFlow</span>
              <span className="tech-tag">OpenAI APIs</span>
              <span className="tech-tag">Langchain</span>
              <span className="tech-tag">Analytics</span>
              <span className="tech-tag">Kubernetes</span>
            </div>
          </div>

          {/* Social Links Card */}
          <div className="bento-card social-card">
            <div className="card-header-bento">
              <h3>Connect</h3>
              <span className="card-icon network-icon">
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="nodeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="linkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 0.6 }} />
                      <stop offset="100%" style={{ stopColor: '#6366f1', stopOpacity: 0.8 }} />
                    </linearGradient>
                    <filter id="nodeGlow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Connection lines */}
                  <g className="connection-lines">
                    <line
                      x1="50"
                      y1="30"
                      x2="25"
                      y2="50"
                      stroke="url(#linkGradient)"
                      strokeWidth="2"
                      className="link link-1"
                    />
                    <line
                      x1="50"
                      y1="30"
                      x2="75"
                      y2="50"
                      stroke="url(#linkGradient)"
                      strokeWidth="2"
                      className="link link-2"
                    />
                    <line
                      x1="50"
                      y1="30"
                      x2="50"
                      y2="70"
                      stroke="url(#linkGradient)"
                      strokeWidth="2"
                      className="link link-3"
                    />
                    <line
                      x1="25"
                      y1="50"
                      x2="50"
                      y2="70"
                      stroke="url(#linkGradient)"
                      strokeWidth="2"
                      className="link link-4"
                    />
                    <line
                      x1="75"
                      y1="50"
                      x2="50"
                      y2="70"
                      stroke="url(#linkGradient)"
                      strokeWidth="2"
                      className="link link-5"
                    />
                    <line
                      x1="25"
                      y1="50"
                      x2="75"
                      y2="50"
                      stroke="url(#linkGradient)"
                      strokeWidth="1.5"
                      className="link link-6"
                      opacity="0.4"
                    />
                  </g>

                  {/* Network nodes */}
                  <g className="network-nodes">
                    {/* Center node (you) */}
                    <circle
                      cx="50"
                      cy="30"
                      r="8"
                      fill="url(#nodeGradient)"
                      className="node center-node"
                      filter="url(#nodeGlow)"
                    />
                    <circle cx="50" cy="30" r="4" fill="#a78bfa" className="node-core" />

                    {/* Left node */}
                    <circle
                      cx="25"
                      cy="50"
                      r="6"
                      fill="url(#nodeGradient)"
                      className="node side-node node-1"
                      filter="url(#nodeGlow)"
                    />
                    <circle cx="25" cy="50" r="3" fill="#a78bfa" className="node-core" />

                    {/* Right node */}
                    <circle
                      cx="75"
                      cy="50"
                      r="6"
                      fill="url(#nodeGradient)"
                      className="node side-node node-2"
                      filter="url(#nodeGlow)"
                    />
                    <circle cx="75" cy="50" r="3" fill="#a78bfa" className="node-core" />

                    {/* Bottom node */}
                    <circle
                      cx="50"
                      cy="70"
                      r="7"
                      fill="url(#nodeGradient)"
                      className="node bottom-node"
                      filter="url(#nodeGlow)"
                    />
                    <circle cx="50" cy="70" r="3.5" fill="#a78bfa" className="node-core" />
                  </g>

                  {/* Data packets traveling along connections */}
                  <g className="data-packets">
                    <circle cx="50" cy="30" r="2" fill="#fbbf24" className="packet packet-1">
                      <animateMotion dur="2s" repeatCount="indefinite">
                        <mpath href="#path1" />
                      </animateMotion>
                    </circle>
                    <circle cx="50" cy="30" r="2" fill="#60a5fa" className="packet packet-2">
                      <animateMotion dur="2.5s" repeatCount="indefinite" begin="0.5s">
                        <mpath href="#path2" />
                      </animateMotion>
                    </circle>
                    <circle cx="50" cy="30" r="2" fill="#ec4899" className="packet packet-3">
                      <animateMotion dur="2.2s" repeatCount="indefinite" begin="1s">
                        <mpath href="#path3" />
                      </animateMotion>
                    </circle>
                  </g>

                  {/* Hidden paths for packet animation */}
                  <defs>
                    <path id="path1" d="M 50 30 L 25 50" />
                    <path id="path2" d="M 50 30 L 75 50" />
                    <path id="path3" d="M 50 30 L 50 70" />
                  </defs>

                  {/* Pulse rings */}
                  <circle
                    cx="50"
                    cy="30"
                    r="10"
                    fill="none"
                    stroke="#6366f1"
                    strokeWidth="1"
                    className="pulse-ring ring-1"
                    opacity="0.6"
                  />
                  <circle
                    cx="50"
                    cy="30"
                    r="10"
                    fill="none"
                    stroke="#8b5cf6"
                    strokeWidth="1"
                    className="pulse-ring ring-2"
                    opacity="0.4"
                  />
                </svg>
              </span>
            </div>
            <div className="social-links-bento">
              <a
                href="https://github.com/liamkande"
                className="social-link-bento"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2A10 10 0 002 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0012 2z" />
                </svg>
                GitHub
              </a>
              <a
                href="https://linkedin.com/in/liamkande"
                className="social-link-bento"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14m-.5 15.5v-5.3a3.26 3.26 0 00-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 011.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 001.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 00-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                </svg>
                LinkedIn
              </a>
              <a href="mailto:liamkande@icloud.com" className="social-link-bento">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Email
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
})
